import type { OnboardingTourStep } from "@/interfaces/tour";

export const TOUR_TARGET_IDS = {
  sendGifts: "tour-send-gifts", // Drawer.tsx:530
  sidebarNav: "tour-sidebar-nav", // Drawer.tsx:259
  sidebarUser: "tour-sidebar-user", // DrawerUserProfile.tsx:51
  accountTabUsers: "tour-account-tab-users", // OrgDetail.tsx:640
  accountPanelUsers: "tour-account-panel-users", // OrgDetail.tsx:651
  walletSummary: "tour-wallet-summary", // GroupCreditsPoolPanel.tsx:242
  walletBuyBtn: "tour-wallet-buy-btn", // GroupCreditsPoolPanel.tsx:302
} as const;

export const TOUR_GEOMETRY = {
  pad: 8,
  /** Card offset, measured from the TARGET rect rather than the padded hole. */
  gap: 14,
  margin: 16,
  cardWidth: 320,
  /** The first step appears in place instead of sliding in from the centre. */
  instantFirstStepMs: 60,
  /** How long a step waits for its targets before falling back to a centered card. */
  targetTimeoutMs: 5000,
} as const;

export const ONBOARDING_TOUR_SEEN_KEY = "onboarding_tour_seen";

export const TOUR_COPY = {
  welcomeHeading: (firstName: string) =>
    firstName ? `Let's do some good, ${firstName}!` : "Let's do some good!",
  welcomeBody:
    "Your account is all set up. Take a quick tour to find your way around, or jump straight in and send your first gifts.",
  welcomeTakeTour: "Take a tour",
  welcomeStartSending: "Start Sending Gifts",
  menuTakeTour: "Take a tour",
  back: "Back",
  next: "Next",
  endTour: "End tour",
  skipTour: "Skip tour",
  closeAria: "Close tour",
  dialogAria: (index1: number, total: number, title: string) =>
    `Tour: step ${index1} of ${total} — ${title}`,
} as const;

export const ONBOARDING_TOUR_STEPS: readonly OnboardingTourStep[] = [
  {
    id: "send-gifts",
    badge: "YOUR FIRST MOVE",
    title: "Send Gifts",
    body: "Start here. Send Gifts launches a campaign — choose a collection, add your recipients, and your gifts go out with a personal message.",
    targets: [TOUR_TARGET_IDS.sendGifts],
    place: "below",
    destination: "dashboard",
  },
  {
    id: "workspace",
    badge: "NAVIGATION",
    title: "Your workspace",
    body: "Everything lives in the left nav: track live sends in My Campaigns, browse your Gifting Calendar, read incoming Thank You Notes, pull Gifting Reports, and browse Collections.",
    targets: [TOUR_TARGET_IDS.sidebarNav],
    place: "right",
    destination: "dashboard",
  },
  {
    id: "account-menu",
    badge: "SETUP",
    title: "Your account menu",
    body: "Your profile and account menu live here — open it to reach your account settings, take a tour again, or log out.",
    targets: [TOUR_TARGET_IDS.sidebarUser],
    place: "right",
    destination: "dashboard",
  },
  {
    id: "users-tab",
    badge: "USERS TAB",
    title: "Add & manage users",
    body: "The Users tab is where you invite teammates, set their roles, and manage who can access your account.",
    targets: [TOUR_TARGET_IDS.accountTabUsers, TOUR_TARGET_IDS.accountPanelUsers],
    place: "right",
    destination: "account-users",
  },
  {
    id: "wallets-tab",
    badge: "WALLETS TAB",
    title: "Top up your wallet",
    body: "Under Wallets you can see your credit balance and use Buy Credits to top up — credits are what fund the gifts you send.",
    targets: [TOUR_TARGET_IDS.walletSummary, TOUR_TARGET_IDS.walletBuyBtn],
    optionalTargets: [TOUR_TARGET_IDS.walletBuyBtn],
    place: "below",
    destination: "account-wallets",
  },
  {
    id: "all-done",
    badge: "ALL DONE",
    title: "You're all set",
    body: "That's the tour. You can replay it anytime from your account menu.",
    targets: [],
    place: "below",
    destination: null,
  },
];
